// components/LoanRequest/LoanRequestKycForm.tsx
import React, { useEffect, useRef, useState } from "react";
import { Card, Steps, Button, Row, Col, Typography, Divider, Spin } from "antd";
import {
  UserOutlined,
  IdcardOutlined,
  FileProtectOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { FormInstance } from "antd/lib/form/Form";
import {
  Customer,
  FormFieldConfig,
  LoanRequest,
  SelectOptionsInterface,
} from "../helpers/shared-interfaces";
import NewBaseForm from "./Form/BaseForm";
import { ghanaCardRegex, invalidGhanaCardMessage } from "../helpers/constants";
import { UploadFile } from "antd/lib/upload/interface";

const { Step } = Steps;
const { Title, Text } = Typography;

interface LoanRequestKycFormProps {
  loanRequest?: LoanRequest;
  maritalStatuses: SelectOptionsInterface[];
  idTypes: SelectOptionsInterface[];
  onSubmit: (formData: any) => void;
  onCancel: () => void;
  isLoading?: boolean;
  customer?: Customer;
  isEditMode?: boolean;
  handleFileUploadChange: (field: string, combinedInfo: string) => void;
  handleDelete: (field: UploadFile) => void;
}

// Every section lays out on the same three-column grid; the form's gutter
// provides the spacing, so individual fields never carry spans or offsets.
const threeColumnGrid = (fields: FormFieldConfig[]): FormFieldConfig[] =>
  fields.map((field) => ({ ...field, colConfig: { span: 8 } }));

const generateLoanKycFormFields = (
  props: LoanRequestKycFormProps,
): {
  personalInfoFields: FormFieldConfig[];
  contactInfoFields: FormFieldConfig[];
  identificationFields: FormFieldConfig[];
  employmentFields: FormFieldConfig[];
  contactPersonFields: FormFieldConfig[];
  bankingFields: FormFieldConfig[];
} => {
  const { idTypes, loanRequest, handleFileUploadChange, handleDelete } = props;
  const customer = loanRequest?.customer;

  const loanKyc = loanRequest?.loanRequestKyc;
  const fullName = `${customer?.firstName || ""} ${
    customer?.middleName || ""
  } ${customer?.lastName || ""}`.trim();

  const primaryPhone =
    customer?.activeCustomerPhones?.find((phone) => phone.isPrimary)?.phone
      ?.number || "";
  const primaryEmail =
    customer?.customerEmails?.find((email) => email.isPrimary)?.email
      ?.address || "";
  const primaryAddress = customer?.customerAddresses?.find(
    (addr) => addr.isPrimary,
  )?.address;
  const residentialAddress = primaryAddress
    ? [primaryAddress.streetName, primaryAddress.city, primaryAddress.region]
        .filter(Boolean)
        .join(", ")
    : "";

  const personalInfoFields: FormFieldConfig[] = [
    {
      id: "fullName",
      name: "fullName",
      inputType: "string",
      rule: "string",
      required: true,
      placeholder: "Full Name",
      label: "Full Name",
      initialValue: loanKyc?.fullName || fullName,
      disabled: !!fullName,
    },
    {
      id: "dateOfBirth",
      name: "dateOfBirth",
      inputType: "date",
      rule: "string",
      required: true,
      placeholder: "Date of Birth",
      label: "Date of Birth",
      initialValue: customer?.dateOfBirth || loanKyc?.dateOfBirth || undefined,
      dateOptions: { limitTo18: true, allowPastDates: true },
      disabled: !!customer?.dateOfBirth,
    },
    {
      id: "gender",
      name: "gender",
      inputType: "select",
      rule: "string",
      placeholder: "Gender",
      label: "Gender",
      initialValue: loanKyc?.gender || customer?.gender || "",
      selectOptions: [
        { id: "male", name: "Male", value: "male" },
        { id: "female", name: "Female", value: "female" },
      ],
      required: true,
      disabled: !!customer?.gender,
    },
    {
      id: "title",
      name: "title",
      inputType: "select",
      rule: "string",
      placeholder: "Title",
      label: "Title",
      initialValue: loanKyc?.title,
      selectOptions: [
        { id: "Mr", name: "Mr", value: "Mr" },
        { id: "Mrs", name: "Mrs", value: "Mrs" },
        { id: "Ms", name: "Ms", value: "Ms" },
        { id: "Dr", name: "Dr", value: "Dr" },
      ],
      required: false,
    },
    {
      id: "maidenName",
      name: "maidenName",
      inputType: "string",
      rule: "string",
      placeholder: "Maiden Name",
      label: "Maiden Name (if applicable)",
      initialValue: loanKyc?.maidenName,
      required: false,
    },
    {
      id: "nationality",
      name: "nationality",
      inputType: "string",
      rule: "string",
      placeholder: "Nationality",
      label: "Nationality",
      initialValue: loanKyc?.nationality,
      required: false,
    },
    {
      id: "hometown",
      name: "hometown",
      inputType: "string",
      rule: "string",
      placeholder: "Hometown",
      label: "Hometown",
      initialValue: loanKyc?.hometown,
      required: false,
    },
    {
      id: "secondaryPhoneNumber",
      name: "secondaryPhoneNumber",
      inputType: "string",
      rule: "string",
      placeholder: "Secondary Phone Number",
      label: "Secondary Phone Number",
      initialValue: loanKyc?.secondaryPhoneNumber,
      required: false,
    },
    {
      id: "educationalQualification",
      name: "educationalQualification",
      inputType: "select",
      rule: "string",
      placeholder: "Educational Qualification",
      label: "Educational Qualification",
      initialValue: loanKyc?.educationalQualification,
      selectOptions: [
        { id: "high_school", name: "High School", value: "high_school" },
        { id: "diploma", name: "Diploma", value: "diploma" },
        { id: "degree", name: "Degree", value: "degree" },
        { id: "post_graduate", name: "Post Graduate", value: "post_graduate" },
      ],
      required: false,
    },
  ];

  const contactInfoFields: FormFieldConfig[] = [
    {
      id: "phoneNumber",
      name: "phoneNumber",
      inputType: "phone",
      rule: "string",
      required: true,
      placeholder: "Phone Number",
      label: "Phone Number",
      initialValue: loanKyc?.phoneNumber || primaryPhone,
    },
    {
      id: "email",
      name: "email",
      inputType: "email",
      rule: "email",
      required: true,
      placeholder: "Email Address",
      label: "Email Address",
      initialValue: loanKyc?.email || primaryEmail,
      normalize: (value: string) => value?.trim(),
    },
    {
      id: "residentialAddress",
      name: "residentialAddress",
      inputType: "string",
      rule: "string",
      required: true,
      placeholder: "Residential Address",
      label: "Residential Address",
      initialValue: loanKyc?.residentialAddress || residentialAddress,
    },
    {
      id: "digitalAddress",
      name: "digitalAddress",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Digital Address (e.g. GA-183-8164)",
      label: "Digital Address",
      initialValue: loanKyc?.digitalAddress,
    },
    {
      id: "postalAddress",
      name: "postalAddress",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Postal Address",
      label: "Postal Address",
      initialValue: loanKyc?.postalAddress,
    },
    {
      id: "townCity",
      name: "townCity",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Town/City",
      label: "Town/City",
      initialValue: loanKyc?.townCity,
    },
    {
      id: "nearestLandmark",
      name: "nearestLandmark",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Nearest Landmark",
      label: "Nearest Landmark",
      initialValue: loanKyc?.nearestLandmark,
    },
    {
      id: "residencyYears",
      name: "residencyYears",
      inputType: "number",
      rule: "number",
      required: false,
      placeholder: "Years in Residence",
      label: "Years in Residence",
      initialValue: loanKyc?.residencyYears,
      elementStyle: { width: "100%" },
    },
    {
      id: "residenceType",
      name: "residenceType",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Residence Type",
      label: "Residence Type",
      initialValue: loanKyc?.residenceType,
      selectOptions: [
        { id: "owned", name: "Owned", value: "owned" },
        { id: "rented", name: "Rented", value: "rented" },
        { id: "family_owned", name: "Family Owned", value: "family_owned" },
        { id: "company_owned", name: "Company Owned", value: "company_owned" },
      ],
    },
  ];

  const identificationFields: FormFieldConfig[] = [
    {
      id: "idType",
      name: "idType",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "ID Type",
      label: "ID Type",
      selectOptions: idTypes,
      disabled: true,
      initialValue: "5",
    },
    {
      id: "ghanaCardFrontUrl",
      name: "ghanaCardFrontUrl",
      inputType: "fileUpload",
      rule: "string",
      initialValue: loanKyc?.ghanaCardFrontUrl || customer?.ghanaCardIdUrl,
      required: false,
      disabled: false,
      label: "Ghana Card (Front)",
      defaultFileList:
        loanKyc?.ghanaCardFrontUrl || customer?.ghanaCardIdUrl
          ? [
              {
                uid: "1",
                name:
                  (loanKyc?.ghanaCardFrontUrl || customer?.ghanaCardIdUrl)
                    ?.split("/")
                    .pop() || "Ghana Card (Front)",
                status: "done",
                url: loanKyc?.ghanaCardFrontUrl || customer?.ghanaCardIdUrl,
              },
            ]
          : [],
      changeHandler: (combinedInfo: string) =>
        handleFileUploadChange("ghanaCardFrontUrl", combinedInfo),
      onFileDelete: handleDelete,
    },
    {
      id: "ghanaCardBackUrl",
      name: "ghanaCardBackUrl",
      inputType: "fileUpload",
      rule: "string",
      initialValue: loanKyc?.ghanaCardBackUrl || customer?.ghanaCardIdBackUrl,
      required: false,
      disabled: false,
      label: "Ghana Card (Back)",
      defaultFileList:
        loanKyc?.ghanaCardBackUrl || customer?.ghanaCardIdBackUrl
          ? [
              {
                uid: "1",
                name:
                  (loanKyc?.ghanaCardBackUrl || customer?.ghanaCardIdBackUrl)
                    ?.split("/")
                    .pop() || "Ghana Card (Back)",
                status: "done",
                url: loanKyc?.ghanaCardBackUrl || customer?.ghanaCardIdBackUrl,
              },
            ]
          : [],
      changeHandler: (combinedInfo: string) =>
        handleFileUploadChange("ghanaCardBackUrl", combinedInfo),
      onFileDelete: handleDelete,
    },
    {
      id: "idNumber",
      name: "idNumber",
      inputType: "string",
      rule: "string",
      required: true,
      placeholder: "ID Number",
      label: "ID Number",
      pattern: ghanaCardRegex,
      patternErrorMessage: invalidGhanaCardMessage,
      initialValue: loanKyc?.idNumber || customer?.ghanaCardNo,
      disabled: !!customer?.ghanaCardNo,
    },
    {
      id: "idExpiryDate",
      name: "idExpiryDate",
      inputType: "date",
      rule: "string",
      required: false,
      placeholder: "ID Expiry Date",
      label: "ID Expiry Date",
      initialValue: loanKyc?.idExpiryDate || customer?.idExpiryDate,
      dateOptions: { limitTo18: false, allowPastDates: false },
      disabled: !!customer?.idExpiryDate,
    },
    {
      id: "idIssueDate",
      name: "idIssueDate",
      inputType: "date",
      rule: "string",
      required: false,
      placeholder: "ID Issue Date",
      label: "ID Issue Date",
      initialValue: loanKyc?.idIssueDate,
      dateOptions: { limitTo18: false, allowPastDates: true },
    },
    {
      id: "tin",
      name: "tin",
      inputType: "string",
      rule: "string",
      required: false,
      placeholder: "Tax Identification Number",
      label: "Tax Identification Number (TIN)",
      initialValue: loanKyc?.tin,
    },
  ];

  const employmentFields: FormFieldConfig[] = [
    {
      id: "occupation",
      name: "occupation",
      inputType: "string",
      rule: "string",
      placeholder: "Occupation",
      label: "Occupation",
      initialValue: loanKyc?.occupation,
      required: true,
    },
    {
      id: "employerName",
      name: "employerName",
      inputType: "string",
      rule: "string",
      placeholder: "Employer's Name",
      label: "Employer's Name",
      initialValue: loanKyc?.employerName || loanRequest?.company?.name,
      required: false,
    },
    {
      id: "monthlyIncome",
      name: "monthlyIncome",
      inputType: "number",
      rule: "number",
      placeholder: "Monthly Income",
      label: "Monthly Income (GHS)",
      initialValue: loanKyc?.monthlyIncome,
      required: true,
      elementStyle: { width: "100%" },
    },
    {
      id: "employmentStatus",
      name: "employmentStatus",
      inputType: "select",
      rule: "string",
      placeholder: "Employment Status",
      label: "Employment Status",
      initialValue: loanKyc?.employmentStatus,
      selectOptions: [
        {
          id: "permanent",
          name: "Permanent/Full-Time",
          value: "permanent",
        },
        { id: "contract", name: "Contract", value: "contract" },
        {
          id: "self_employed",
          name: "Self-Employed",
          value: "self_employed",
        },
        {
          id: "part_time_casual",
          name: "Part-Time/Casual",
          value: "part_time_casual",
        },
        { id: "unemployed", name: "Unemployed", value: "unemployed" },
      ],
      required: false,
    },
    {
      id: "designation",
      name: "designation",
      inputType: "string",
      rule: "string",
      placeholder: "Designation",
      label: "Designation",
      initialValue: loanKyc?.designation,
      required: false,
    },
    {
      id: "dateEmployed",
      name: "dateEmployed",
      inputType: "date",
      rule: "string",
      placeholder: "Date Employed",
      label: "Date Employed",
      initialValue: loanKyc?.dateEmployed,
      required: false,
      dateOptions: { limitTo18: false, allowPastDates: true },
    },
    {
      id: "employeeStaffId",
      name: "employeeStaffId",
      inputType: "string",
      rule: "string",
      placeholder: "Employee/Staff ID",
      label: "Employee/Staff ID",
      initialValue: loanKyc?.employeeStaffId,
      required: false,
    },
    {
      id: "officeLocation",
      name: "officeLocation",
      inputType: "string",
      rule: "string",
      placeholder: "Office Location",
      label: "Office Location",
      initialValue: loanKyc?.officeLocation,
      required: false,
    },
    {
      id: "officeLandmark",
      name: "officeLandmark",
      inputType: "string",
      rule: "string",
      placeholder: "Office Nearest Landmark",
      label: "Office Nearest Landmark",
      initialValue: loanKyc?.officeLandmark,
      required: false,
    },
    {
      id: "officeTownCity",
      name: "officeTownCity",
      inputType: "string",
      rule: "string",
      placeholder: "Office Town/City",
      label: "Office Town/City",
      initialValue: loanKyc?.officeTownCity,
      required: false,
    },
    {
      id: "officeDigitalAddress",
      name: "officeDigitalAddress",
      inputType: "string",
      rule: "string",
      placeholder: "Office Digital Address",
      label: "Office Digital Address",
      initialValue: loanKyc?.officeDigitalAddress,
      required: false,
    },
    {
      id: "employerEmail",
      name: "employerEmail",
      inputType: "email",
      rule: "email",
      placeholder: "Employer Email",
      label: "Employer Email",
      initialValue: loanKyc?.employerEmail,
      required: false,
    },
    {
      id: "employerPhone",
      name: "employerPhone",
      inputType: "string",
      rule: "string",
      placeholder: "Employer Phone",
      label: "Employer Phone",
      initialValue: loanKyc?.employerPhone,
      required: false,
    },
    {
      id: "netMonthlyIncome",
      name: "netMonthlyIncome",
      inputType: "number",
      rule: "number",
      placeholder: "Net Monthly Income",
      label: "Net Monthly Income (GHS)",
      initialValue: loanKyc?.netMonthlyIncome,
      required: false,
      elementStyle: { width: "100%" },
    },
    {
      id: "totalAllowance",
      name: "totalAllowance",
      inputType: "number",
      rule: "number",
      placeholder: "Total Allowance",
      label: "Total Allowance (GHS)",
      initialValue: loanKyc?.totalAllowance,
      required: false,
      elementStyle: { width: "100%" },
    },
    {
      id: "totalDeductions",
      name: "totalDeductions",
      inputType: "number",
      rule: "number",
      placeholder: "Total Deductions",
      label: "Total Deductions (GHS)",
      initialValue: loanKyc?.totalDeductions,
      required: false,
      elementStyle: { width: "100%" },
    },
    {
      id: "salaryPaymentDay",
      name: "salaryPaymentDay",
      inputType: "string",
      rule: "string",
      placeholder: "e.g. 25th of every month",
      label: "Day Salary Is Paid",
      initialValue: loanKyc?.salaryPaymentDay,
      required: false,
    },
  ];

  // const nextOfKinFields: FormFieldConfig[] = [
  //   {
  //     id: "nextOfKinName",
  //     name: "nextOfKinName",
  //     inputType: "string",
  //     rule: "string",
  //     placeholder: "Name",
  //     label: "Name",
  //     initialValue:
  //       loanKyc?.nextOfKinName ||
  //       customer?.customerBackgroundInformation?.nextOfKinName,
  //     required: false,
  //     colConfig: { span: 6 },
  //   },
  //       {
  //     id: "nextOfKinRelationship",
  //     name: "nextOfKinRelationship",
  //     inputType: "string",
  //     rule: "string",
  //     placeholder: "Relationship",
  //     label: "Relationship",
  //     initialValue: loanKyc?.nextOfKinRelationship,
  //     required: false,
  //     colConfig: { span: 6, offset: 1 },
  //   },
  //   {
  //     id: "nextOfKinPhone",
  //     name: "nextOfKinPhone",
  //     inputType: "phone",
  //     rule: "string",
  //     placeholder: "Phone Number",
  //     label: "Phone Number",
  //     initialValue:
  //       loanKyc?.nextOfKinPhone ||
  //       customer?.customerBackgroundInformation?.nextOfKinPhoneNumber,
  //     required: false,
  //     colConfig: { span: 6, offset: 1 },
  //   },
  //   {
  //     id: "nextOfKinAddress",
  //     name: "nextOfKinAddress",
  //     inputType: "string",
  //     rule: "string",
  //     placeholder: "Address",
  //     label: "Address",
  //     initialValue: loanKyc?.nextOfKinAddress,
  //     required: false,
  //     colConfig: { span: 6 },
  //   },
  // ];

  //   const collateralFields: FormFieldConfig[] = [
  //   {
  //     id: "collateralType",
  //     name: "collateralType",
  //     inputType: "select",
  //     rule: "string",
  //     placeholder: "Collateral Type",
  //     label: "Collateral Type",
  //     initialValue: loanKyc?.collateralType,
  //     selectOptions: [
  //       { id: "property", name: "Property", value: "property" },
  //       { id: "vehicle", name: "Vehicle", value: "vehicle" },
  //       { id: "savings", name: "Savings/Fixed Deposit", value: "savings" },
  //       { id: "other", name: "Other", value: "other" },
  //     ],
  //     required: false,
  //     colConfig: { span: 6 },
  //   },
  //   {
  //     id: "collateralValue",
  //     name: "collateralValue",
  //     inputType: "number",
  //     rule: "number",
  //     placeholder: "Collateral Value",
  //     label: "Collateral Value (GHS)",
  //     initialValue: loanKyc?.collateralValue,
  //     required: false,
  //     colConfig: { span: 6, offset: 1 },
  //     elementStyle: { width: "100%" },
  //   },
  //   {
  //     id: "collateralDescription",
  //     name: "collateralDescription",
  //     inputType: "textArea",
  //     rule: "string",
  //     placeholder: "Collateral Description",
  //     label: "Collateral Description",
  //     initialValue: loanKyc?.collateralDescription,
  //     required: false,
  //     colConfig: { span: 6, offset: 1 },
  //   },
  // ];

  const bankingFields: FormFieldConfig[] = [
    {
      id: "bankName",
      name: "bankName",
      inputType: "string",
      rule: "string",
      placeholder: "Bank Name",
      label: "Bank Name",
      initialValue: loanKyc?.bankName,
      required: false,
    },
    {
      id: "bankAccountNumber",
      name: "bankAccountNumber",
      inputType: "string",
      rule: "string",
      placeholder: "Account Number",
      label: "Account Number",
      initialValue: loanKyc?.bankAccountNumber,
      required: false,
    },
    {
      id: "bankAccountName",
      name: "bankAccountName",
      inputType: "string",
      rule: "string",
      placeholder: "Account Name",
      label: "Account Name",
      initialValue: loanKyc?.bankAccountName,
      required: false,
    },
    {
      id: "modeOfRepayment",
      name: "modeOfRepayment",
      inputType: "select",
      rule: "string",
      placeholder: "Mode of Repayment",
      label: "Mode of Repayment",
      initialValue: loanKyc?.modeOfRepayment,
      selectOptions: [
        { id: "direct_debit", name: "Direct Debit", value: "direct_debit" },
        {
          id: "standing_order",
          name: "Standing Order",
          value: "standing_order",
        },
        {
          id: "account_deduction",
          name: "Account Deduction",
          value: "account_deduction",
        },
        { id: "cash", name: "Cash", value: "cash" },
        { id: "cheque", name: "Cheque", value: "cheque" },
      ],
      required: false,
    },
    {
      id: "secondaryRepaymentMode",
      name: "secondaryRepaymentMode",
      inputType: "select",
      rule: "string",
      placeholder: "Secondary Repayment Mode",
      label: "Secondary Repayment Mode",
      initialValue: loanKyc?.secondaryRepaymentMode,
      selectOptions: [
        { id: "direct_debit", name: "Direct Debit", value: "direct_debit" },
        {
          id: "standing_order",
          name: "Standing Order",
          value: "standing_order",
        },
        {
          id: "account_deduction",
          name: "Account Deduction",
          value: "account_deduction",
        },
      ],
      required: false,
    },
    {
      id: "disbursementMode",
      name: "disbursementMode",
      inputType: "select",
      rule: "string",
      placeholder: "Mode of Disbursement",
      label: "Mode of Disbursement",
      initialValue: loanKyc?.disbursementMode,
      selectOptions: [
        {
          id: "lender_account",
          name: "Lender Account",
          value: "lender_account",
        },
        {
          id: "other_bank",
          name: "Other Bank Transfer",
          value: "other_bank",
        },
      ],
      required: false,
    },
    {
      id: "disbursementIsSalaryAccount",
      name: "disbursementIsSalaryAccount",
      inputType: "select",
      rule: "string",
      placeholder: "Is disbursement account a salary account?",
      label: "Is Disbursement Account a Salary Account?",
      // Stored as a boolean on the backend; rendered as a yes/no select and
      // coerced back to a boolean on submit (see normalizeKycData).
      initialValue:
        loanKyc?.disbursementIsSalaryAccount === true
          ? "yes"
          : loanKyc?.disbursementIsSalaryAccount === false
            ? "no"
            : undefined,
      selectOptions: [
        { id: "yes", name: "Yes", value: "yes" },
        { id: "no", name: "No", value: "no" },
      ],
      required: false,
    },
    {
      id: "fundUsedToSecure",
      name: "fundUsedToSecure",
      inputType: "select",
      rule: "string",
      placeholder: "Fund Used to Secure Loan",
      label: "Fund Used to Secure Loan",
      initialValue: loanKyc?.fundUsedToSecure,
      selectOptions: [
        { id: "tier_3", name: "Tier 3/Provident Fund", value: "tier_3" },
        {
          id: "savings_booster",
          name: "Savings Booster",
          value: "savings_booster",
        },
        { id: "both", name: "Both", value: "both" },
      ],
      required: false,
    },
    {
      id: "notes",
      name: "notes",
      inputType: "textArea",
      rule: "string",
      placeholder: "Additional Notes",
      label: "Additional Notes",
      initialValue: loanKyc?.notes,
      required: false,
    },
  ];

  // Contact person (secured/semi-secured) doubles as the guarantor on
  // unsecured loans; the application form relabels the heading by security
  // type. The name/phone/relationship/address reuse the next-of-kin columns.
  const contactPersonFields: FormFieldConfig[] = [
    {
      id: "contactPersonTitle",
      name: "contactPersonTitle",
      inputType: "select",
      rule: "string",
      placeholder: "Title",
      label: "Title",
      initialValue: loanKyc?.contactPersonTitle,
      selectOptions: [
        { id: "Mr", name: "Mr", value: "Mr" },
        { id: "Mrs", name: "Mrs", value: "Mrs" },
        { id: "Ms", name: "Ms", value: "Ms" },
        { id: "Dr", name: "Dr", value: "Dr" },
      ],
      required: false,
    },
    {
      id: "nextOfKinName",
      name: "nextOfKinName",
      inputType: "string",
      rule: "string",
      placeholder: "Full Name",
      label: "Full Name",
      initialValue: loanKyc?.nextOfKinName,
      required: false,
    },
    {
      id: "nextOfKinRelationship",
      name: "nextOfKinRelationship",
      inputType: "string",
      rule: "string",
      placeholder: "Relationship to Borrower",
      label: "Relationship to Borrower",
      initialValue: loanKyc?.nextOfKinRelationship,
      required: false,
    },
    {
      id: "nextOfKinPhone",
      name: "nextOfKinPhone",
      inputType: "phone",
      rule: "string",
      placeholder: "Mobile Number",
      label: "Mobile Number",
      initialValue: loanKyc?.nextOfKinPhone,
      required: false,
    },
    {
      id: "contactPersonEmail",
      name: "contactPersonEmail",
      inputType: "email",
      rule: "email",
      placeholder: "Email Address",
      label: "Email Address",
      initialValue: loanKyc?.contactPersonEmail,
      required: false,
      normalize: (value: string) => value?.trim(),
    },
    {
      id: "contactPersonOccupation",
      name: "contactPersonOccupation",
      inputType: "string",
      rule: "string",
      placeholder: "Occupation",
      label: "Occupation",
      initialValue: loanKyc?.contactPersonOccupation,
      required: false,
    },
    {
      id: "contactPersonPlaceOfWork",
      name: "contactPersonPlaceOfWork",
      inputType: "string",
      rule: "string",
      placeholder: "Place of Work",
      label: "Place of Work",
      initialValue: loanKyc?.contactPersonPlaceOfWork,
      required: false,
    },
    {
      id: "nextOfKinAddress",
      name: "nextOfKinAddress",
      inputType: "string",
      rule: "string",
      placeholder: "Residential Address",
      label: "Residential Address",
      initialValue: loanKyc?.nextOfKinAddress,
      required: false,
    },
  ];

  return {
    personalInfoFields: threeColumnGrid(personalInfoFields),
    contactInfoFields: threeColumnGrid(contactInfoFields),
    identificationFields: threeColumnGrid(identificationFields),
    employmentFields: threeColumnGrid(employmentFields),
    contactPersonFields: threeColumnGrid(contactPersonFields),
    // collateralFields,
    bankingFields: threeColumnGrid(bankingFields),
  };
};

const mapCustomerToFormValues = (
  customer: Customer,
  loanKyc?: LoanRequest["loanRequestKyc"],
) => ({
  ...customer,
  idType: "5",
  idNumber: loanKyc?.idNumber || customer.ghanaCardNo,
});

// The salary-account question renders as a yes/no select but is a boolean on
// the backend; coerce it before handing the payload to the mutation.
const normalizeKycData = (data: Record<string, any>) => {
  const salary = data.disbursementIsSalaryAccount;
  return {
    ...data,
    disbursementIsSalaryAccount:
      salary === "yes" || salary === true
        ? true
        : salary === "no" || salary === false
          ? false
          : undefined,
  };
};

const LoanRequestKycForm: React.FC<LoanRequestKycFormProps> = ({
  loanRequest,
  maritalStatuses,
  idTypes,
  onSubmit,
  onCancel,
  isLoading = false,
  customer,
  isEditMode = false,
  handleFileUploadChange,
  handleDelete,
}) => {
  const formRef = useRef<FormInstance>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<any>({});
  const [fieldsToHide, setFieldsToHide] = useState<string[]>([]); // Add this

  const {
    personalInfoFields,
    contactInfoFields,
    identificationFields,
    employmentFields,
    contactPersonFields,
    // collateralFields,
    bankingFields,
  } = generateLoanKycFormFields({
    loanRequest,
    maritalStatuses,
    idTypes,
    onSubmit,
    onCancel,
    isEditMode,
    handleFileUploadChange,
    handleDelete,
  });

  const formSections = [
    {
      title: "Personal Information",
      icon: <UserOutlined />,
      fields: personalInfoFields,
      description: "Basic personal details and demographics",
    },
    {
      title: "Contact Information",
      icon: <UserOutlined />,
      fields: contactInfoFields,
      description: "Contact details and address information",
    },
    {
      title: "Identification",
      icon: <IdcardOutlined />,
      fields: identificationFields,
      description: "Identity documents and verification",
    },
    {
      title: "Employment Details",
      icon: <FileProtectOutlined />,
      fields: employmentFields,
      description: "Employment and income information",
    },
    {
      title: "Contact Person / Guarantor",
      icon: <UserOutlined />,
      fields: contactPersonFields,
      description: "Contact person (guarantor on unsecured loans)",
    },
    //     {
    //   title: "Collateral Information",
    //   icon: <DollarOutlined />,
    //   fields: collateralFields,
    //   description: "Collateral details (if applicable)",
    // },
    {
      title: "Banking & Repayment",
      icon: <BankOutlined />,
      fields: bankingFields,
      description: "Banking details and repayment preferences",
    },
  ];

  const saveCurrentStepData = () => {
    if (!formRef.current) return;

    const currentStepData = formRef.current.getFieldsValue(true);
    setFormData((prevData: Record<string, any>) => ({
      ...prevData,
      ...currentStepData,
    }));
  };

  useEffect(() => {
    if (formRef.current && loanRequest?.loanRequestKyc && isEditMode) {
      const kycData = loanRequest.loanRequestKyc;
      setFormData(kycData);
      formRef.current.setFieldsValue(kycData);
    } else if (
      formRef.current &&
      customer &&
      Object.keys(customer).length > 0 &&
      !isEditMode
    ) {
      const mappedCustomerData = mapCustomerToFormValues(
        customer,
        loanRequest?.loanRequestKyc,
      );
      setFormData(mappedCustomerData);
      formRef.current.setFieldsValue(mappedCustomerData);
    }
  }, [customer, loanRequest, isEditMode]);

  useEffect(() => {
    if (formRef.current && Object.keys(formData).length > 0) {
      formRef.current.setFieldsValue(formData);
    }
  }, [currentStep, formData]);

  const handleNext = async () => {
    if (!formRef.current) return;

    try {
      const currentFields = formSections[currentStep].fields.map(
        (field) => field.name,
      );
      await formRef.current.validateFields(currentFields);

      saveCurrentStepData();
      setCurrentStep((prev) => prev + 1);
    } catch (error) {
      console.error("Validation error:", error);
    }
  };

  const handlePrev = () => {
    saveCurrentStepData();
    setCurrentStep((prev) => prev - 1);
  };

  const handleStepClick = (step: number) => {
    if (isEditMode) {
      saveCurrentStepData();
      setCurrentStep(step);
    }
  };

  const handleSaveCurrentStep = async () => {
    if (!formRef.current) return;

    try {
      const currentFields = formSections[currentStep].fields.map(
        (field) => field.name,
      );
      await formRef.current.validateFields(currentFields);

      const allFormValues = formRef.current.getFieldsValue(true);
      const mergedFormData = {
        ...formData,
        ...allFormValues,
      };

      const kycData = {
        loanRequestId: loanRequest?.id,
        ...mergedFormData,
      };
      onSubmit(normalizeKycData(kycData));
    } catch (error) {
      console.error("Form validation error:", error);
    }
  };

  const handleSubmit = async () => {
    if (!formRef.current) return;

    try {
      await formRef.current.validateFields();

      const finalFormData = formRef.current.getFieldsValue(true);
      const mergedFormData = {
        ...formData,
        ...finalFormData,
      };

      const kycData = {
        loanRequestId: loanRequest?.id,
        ...mergedFormData,
      };
      onSubmit(normalizeKycData(kycData));
    } catch (error) {
      console.error("Form validation error:", error);
    }
  };

  useEffect(() => {
    if (loanRequest?.loanRequestKyc?.nextOfKinRelationship !== "other") {
      setFieldsToHide(["nextOfKinRelationshipOther"]);
    } else {
      setFieldsToHide([]);
    }
  }, [loanRequest]);

  const currentSection = formSections[currentStep];
  const isLastStep = currentStep === formSections.length - 1;

  return (
    <Spin spinning={isLoading}>
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <Card style={{ marginBottom: 24 }}>
          <Steps
            current={currentStep}
            size="small"
            direction="horizontal"
            onChange={isEditMode ? handleStepClick : undefined}
            style={{ cursor: isEditMode ? "pointer" : "default" }}
          >
            {formSections.map((section, index) => (
              <Step key={index} title={section.title} icon={section.icon} />
            ))}
          </Steps>
        </Card>

        <Card
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {currentSection.icon}
              <Title level={4} style={{ margin: 0 }}>
                {currentSection.title}
              </Title>
            </div>
          }
          extra={
            <Text type="secondary">
              Step {currentStep + 1} of {formSections.length}
            </Text>
          }
        >
          <Text type="secondary">{currentSection.description}</Text>

          <Divider style={{ marginTop: 16, marginBottom: 24 }} />

          <NewBaseForm
            layout="vertical"
            reference={formRef}
            fieldsToHide={fieldsToHide}
            fields={currentSection.fields}
            gutter={[24, 0]}
          />

          <div
            style={{
              marginTop: 32,
              display: "flex",
              justifyContent: "space-between",
              borderTop: "1px solid #f0f0f0",
              paddingTop: 16,
            }}
          >
            <Button onClick={onCancel} style={{ minWidth: 100 }}>
              Cancel
            </Button>

            <div style={{ display: "flex", gap: 8 }}>
              {isEditMode ? (
                <Button
                  type="primary"
                  onClick={handleSaveCurrentStep}
                  loading={isLoading}
                  style={{ minWidth: 120 }}
                >
                  Save
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handlePrev}
                    disabled={currentStep === 0}
                    style={{ minWidth: 100 }}
                  >
                    Previous
                  </Button>

                  {isLastStep ? (
                    <Button
                      type="primary"
                      onClick={handleSubmit}
                      loading={isLoading}
                      style={{ minWidth: 120 }}
                    >
                      Submit KYC
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      onClick={handleNext}
                      style={{ minWidth: 100 }}
                    >
                      Next
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </Card>

        <Card size="small" style={{ marginTop: 16 }}>
          <Row gutter={16} align="middle">
            <Col>
              <Text strong>Progress: </Text>
            </Col>
            <Col flex="auto">
              <div
                style={{
                  background: "#f0f0f0",
                  height: 8,
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    background: "#1890ff",
                    height: "100%",
                    width: `${
                      ((currentStep + 1) / formSections.length) * 100
                    }%`,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </Col>
            <Col>
              <Text type="secondary">
                {Math.round(((currentStep + 1) / formSections.length) * 100)}%
                Complete
              </Text>
            </Col>
          </Row>
        </Card>
      </div>
    </Spin>
  );
};

export { LoanRequestKycForm };
